import React, { useCallback } from 'react'
import { Checkbox } from '../Checkbox'
import { Wrapper, RadioGroupLayout, RadioWrapper } from './components'
import { CheckboxProps } from '../Checkbox/Checkbox'

export interface RadioItem<ValueType extends number | string, ExtraType = any> {
  value: ValueType
  label: string
  gridArea?: string
  justifySelf?: string
  alignSelf?: string
  extra?: ExtraType
}

type CheckboxStyleProps = Partial<
  Pick<
    CheckboxProps,
    | 'boxWidth'
    | 'boxHeight'
    | 'boxColor'
    | 'boxBackground'
    | 'boxBorderRadius'
    | 'checkedIconProps'
    | 'labelPosition'
  >
>
export interface RadioGroupProps<
  ValueType extends number | string,
  ExtraType = any
> {
  className?: string
  value: ValueType
  radios: RadioItem<ValueType, ExtraType>[]

  layout?: RadioGroupLayout
  checkboxProps?: CheckboxStyleProps

  fontSize?: string

  onChange: (
    value: ValueType,
    radio: RadioItem<ValueType, ExtraType>
  ) => boolean | void
}

export const RadioGroup = <ValueType extends number | string, ExtraType = any>({
  className,
  value,
  radios,

  layout = {
    type: 'flex',
    direction: 'vertical',
    gap: '6px',
  },
  checkboxProps = {},

  fontSize = '1em',

  onChange,
}: RadioGroupProps<ValueType, ExtraType>) => {
  const handleRadioClicked = useCallback(
    (radio: RadioItem<ValueType, ExtraType>) => {
      onChange(radio.value, radio)
    },
    [onChange]
  )

  return (
    <Wrapper className={className} layout={layout}>
      {radios.map(radio => (
        <RadioWrapper
          key={radio.value}
          className="--radio-wrapper"
          gridArea={radio.gridArea}
          justifySelf={radio.justifySelf}
          alignSelf={radio.alignSelf}
        >
          <Checkbox
            checkedIconProps={{ color: 'rgba(0, 0, 0, 0)' }}
            className="--radio-group-checkbox"
            checked={radio.value === value}
            label={radio.label}
            boxBorderRadius="999px"
            fontSize={fontSize}
            {...checkboxProps}
            onChange={() => handleRadioClicked(radio)}
          />
        </RadioWrapper>
      ))}
    </Wrapper>
  )
}
